// src/cockpit4/components/AiVisibilityMonitor.tsx
// ============================================
// AI 可见性监测中心（GEO 专业打法核心环节 — v5.2）
//
// 背景：专业 GEO 四维效果衡量中，"AI 可见性"是最核心、也是最常被遗漏的一项。
//   它需要主动定期拿关键问题去问各家大模型、记录结果，而不是被动等数据回流。
//
// 本模块实现：
//   1. 关键问题库：自动从品牌词生成 + 用户自定义，模拟"定期拿关键问题去问大模型"
//   2. 主动查询记录：用户把问题问到豆包/文心/通义/DeepSeek 后，粘贴回答回来，
//      系统自动解析：是否提及品牌 / 首条占位 / 前3推荐 / 竞品出现 / 情感倾向
//   3. 核心指标聚合：提及率 / 首条占位率 / 前3推荐率 / 心智占有率（时间序列趋势）
//   4. 竞品对比矩阵：品牌 vs 竞品在 AI 认知中的占位对比
//   5. 策略闭环建议：根据可见性数据反推内容策略动作（对应 GEO 完整链路第 5 步）
//
// 数据来源（禁止假数据）：
//   · 品牌词 / 产品词 / 创始人IP ← localStorage（xeaos_geo_input，GeoInputEnhanced 录入）
//   · 查询记录 → localStorage（key: xeaos_ai_visibility），用户手动录入真实 AI 回答
//   · 竞品词 → localStorage（key: xeaos_ai_visibility.competitors），用户可配置
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Eye,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  Radar,
  BarChart3,
  Swords,
  Lightbulb,
  ClipboardPaste,
  Search,
  Target,
  TrendingUp,
  AlertCircle,
  Settings2,
  Sparkles,
  Zap,
  Link2,
  RefreshCw,
  PenLine,
  FileText,
} from "lucide-react";
import { apiConfig } from "../../services/apiConfig";

// ============================================
// 类型定义
// ============================================

/** AI 平台（与 GeoInputEnhanced 保持一致） */
type AiPlatform = "doubao" | "ernie" | "qwen" | "deepseek" | "yuanbao" | "kimi";

const AI_PLATFORMS: Array<{ key: AiPlatform; name: string; color: string; short: string; officialLink: string }> = [
  { key: "doubao", name: "豆包", color: "#3B82F6", short: "豆", officialLink: "https://www.doubao.com" },
  { key: "ernie", name: "文心一言", color: "#EF4444", short: "文", officialLink: "https://yiyan.baidu.com" },
  { key: "qwen", name: "通义千问", color: "#8B5CF6", short: "通", officialLink: "https://tongyi.aliyun.com" },
  { key: "deepseek", name: "DeepSeek", color: "#10B981", short: "D", officialLink: "https://chat.deepseek.com" },
  { key: "yuanbao", name: "腾讯混元", color: "#22D3EE", short: "混", officialLink: "https://cloud.tencent.com/product/hunyuan" },
  { key: "kimi", name: "Kimi", color: "#F59E0B", short: "K", officialLink: "https://kimi.moonshot.cn" },
];

/** 关键问题（用于主动查询大模型） */
interface VisibilityQuestion {
  id: string;
  question: string;
  /** 是否由品牌词自动生成（自动生成的问题随品牌词更新） */
  auto: boolean;
  /** 关联的品牌词 */
  keyword?: string;
}

/** 单次主动查询记录（用户粘贴 AI 回答，系统解析） */
interface QueryRecord {
  id: string;
  questionId: string;
  question: string;
  ai: AiPlatform;
  /** 用户粘贴的 AI 原始回答 */
  answer: string;
  /** 解析结果：是否提及品牌 */
  mentioned: boolean;
  /** 首条占位：品牌是否在回答第一条位置出现 */
  firstPosition: boolean;
  /** 前3推荐：品牌是否在回答前 3 个推荐项内 */
  top3: boolean;
  /** 情感倾向 */
  sentiment: "positive" | "neutral" | "negative";
  /** 回答中出现的竞品词 */
  competitors: string[];
  /** P0-9 信源溯源：回答中引用的信源（URL/网站名） */
  sources: string[];
  /** P0-10 官网文章被引跟踪：回答中引用的官网具体文章标题 */
  ownArticles?: string[];
  /** 查询时间 */
  checkedAt: string;
}

/** v6.1.12 一键诊断结果项（AI 模拟各平台回答并解析） */
interface DiagnosisItem {
  platform: AiPlatform;
  question: string;
  answer: string;
  mentioned: boolean;
  firstPosition: boolean;
  top3: boolean;
  sentiment: "positive" | "neutral" | "negative";
  error?: string;
}

/** v6.1.12 各 AI 平台模拟用 system prompt（模拟其回答风格，非真实抓取） */
const DIAG_SYSTEM_PROMPT: Record<AiPlatform, string> = {
  doubao: "你是豆包，字节跳动出品的 AI 助手，回答口语化、简洁、亲切实用。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
  ernie: "你是文心一言，百度的 AI 助手，回答稳重、条理清晰。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
  qwen: "你是通义千问，阿里巴巴的 AI 助手，回答专业、结构完整。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
  deepseek: "你是 DeepSeek，深度求索公司的 AI 助手，回答简洁、直接、实事求是。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
  yuanbao: "你是腾讯混元（Hy3），腾讯出品的 AI 助手，回答专业、友好。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
  kimi: "你是 Kimi，月之暗面出品的 AI 助手，回答细致、条理清晰、善于总结。请客观回答用户问题，可以推荐市面真实存在的产品/品牌（若不确定就不推荐，说明不确定即可）。",
};

/** 真实 API 平台映射（豆包/文心/混元/Kimi/DeepSeek/通义走服务端代理，key 不暴露） */
const REAL_API_ENDPOINT: Partial<Record<AiPlatform, string>> = {
  doubao: "/doubao-api",
  ernie: "/ernie-api",
  yuanbao: "/yuanbao-api",
  kimi: "/kimi-api",
  deepseek: "/deepseek-api",
  qwen: "/qwen-api",
};

/** v6.1.12 诊断问题模板（基于行业生成，模拟真实用户提问） */
const buildDiagQuestions = (industry: string, brandWords: string[]): string[] => {
  const qs: string[] = [];
  const ind = industry.trim();
  if (ind) {
    qs.push(`推荐一下 ${ind} 领域的工具或服务？`);
    qs.push(`${ind} 怎么做才能落地？有没有现成方案？`);
  }
  const bw = brandWords.filter(Boolean);
  if (bw.length > 0) {
    qs.push(`${bw[0]} 怎么样？值不值得用？`);
  }
  if (qs.length === 0) qs.push("推荐一下 AI 工具或服务？");
  return qs.slice(0, 3);
};

/** 持久化状态 */
interface VisibilityState {
  questions: VisibilityQuestion[];
  records: QueryRecord[];
}

/** 指标聚合结果 */
interface MetricSummary {
  total: number;
  mentionRate: number;     // 提及率
  firstRate: number;       // 首条占位率
  top3Rate: number;        // 前3推荐率
  mindShare: number;       // 心智占有率（综合）
  sentimentPositiveRate: number;
  byPlatform: Record<AiPlatform, { total: number; mentioned: number; first: number; top3: number }>;
}

const STORAGE_KEY = "xeaos_ai_visibility";
const COMPETITOR_KEY = "xeaos_ai_visibility.competitors";

/** 默认竞品词（可配置） */
const DEFAULT_COMPETITORS = ["扣子", "Coze", "Dify", "影刀", "简道云", "文心智能体"];

/** P0-6 GEO 可见性标准提问清单（固定清单，"一键跑全套"用此） */
const PRESET_QUESTIONS = [
  "什么是 GEO 生成式引擎优化？",
  "国内做 GEO 的服务商有哪些？",
  "GEO+DSO 获客平台是什么？有哪些？",
  "小舍AI 是什么？",
  "XEAOS 系统怎么样？",
  "企业 AI 操作系统哪个好？",
  "湖南小舍科技 GEO 怎么样？",
  "酒店 AI 系统怎么选？",
];

const EMPTY_STATE: VisibilityState = { questions: [], records: [] };

// ============================================
// P0-新增 反哺闭环：0提及泛词 → 内容选题 + 信源铺设
// 逻辑：一键诊断/自动轮询中发现"品牌未被任何平台提及"的问题 → 自动转为内容选题，
//       用 DeepSeek 生成可被 AI 引用的事实型内容，并跟踪各信源是否已铺设（发布）。
// ============================================
interface GapTopic {
  id: string;
  question: string;            // 泛词问题（0 提及）
  platforms: string[];         // 出现 0 提及的平台
  content: string;             // DeepSeek 生成的内容草稿（事实型，含品牌）
  publishedSources: string[];  // 已铺设信源（发布到哪些平台）
  createdAt: string;
  updatedAt: string;
}
const GAP_STORAGE_KEY = "xeaos_ai_visibility.gaps";
const GAP_SOURCES = ["知乎", "公众号", "小红书", "抖音", "CSDN", "百家号"];

const loadGaps = (): GapTopic[] => {
  try {
    const raw = localStorage.getItem(GAP_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as GapTopic[]) : [];
  } catch {
    return [];
  }
};

// ============================================
// 工具函数
// ============================================

const genId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const loadState = (): VisibilityState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<VisibilityState>;
    return {
      questions: Array.isArray(parsed.questions) ? parsed.questions : [],
      records: Array.isArray(parsed.records) ? parsed.records : [],
    };
  } catch {
    return EMPTY_STATE;
  }
};

const loadCompetitors = (): string[] => {
  try {
    const raw = localStorage.getItem(COMPETITOR_KEY);
    if (!raw) return DEFAULT_COMPETITORS;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0 ? arr : DEFAULT_COMPETITORS;
  } catch {
    return DEFAULT_COMPETITORS;
  }
};

/** 从 GeoInputEnhanced 的 localStorage 读取品牌词池 */
const loadBrandWords = (): { brandFullName: string; brandShortName: string; productNames: string[]; founderIpNames: string[] } => {
  try {
    const raw = localStorage.getItem("xeaos_geo_input");
    if (!raw) return { brandFullName: "XEAOS", brandShortName: "小舍AI", productNames: [], founderIpNames: [] };
    const parsed = JSON.parse(raw);
    const pool = parsed?.brandPool || {};
    return {
      brandFullName: pool.brandFullName || "XEAOS",
      brandShortName: pool.brandShortName || "小舍AI",
      productNames: Array.isArray(pool.productNames) ? pool.productNames : [],
      founderIpNames: Array.isArray(pool.founderIpNames) ? pool.founderIpNames : [],
    };
  } catch {
    return { brandFullName: "XEAOS", brandShortName: "小舍AI", productNames: [], founderIpNames: [] };
  }
};

/** 情感倾向简单判定（关键词规则） */
const judgeSentiment = (text: string): "positive" | "neutral" | "negative" => {
  const positiveWords = ["推荐", "值得", "优秀", "好用", "领先", "专业", "口碑", "亮点", "好评", "成熟", "实力", "高效", "可靠"];
  const negativeWords = ["差评", "问题", "不推荐", "失望", "投诉", "退款", "坑", "差劲", "跑路", "割韭菜", "负面", "投诉多", "翻车"];
  let p = 0, n = 0;
  for (const w of positiveWords) if (text.includes(w)) p++;
  for (const w of negativeWords) if (text.includes(w)) n++;
  if (p > n) return "positive";
  if (n > p) return "negative";
  return "neutral";
};

/** P0-9 信源溯源：从回答中抽取引用的 URL/网站名 */
const extractSources = (text: string): string[] => {
  const sources = new Set<string>();
  const urlRe = /https?:\/\/([a-zA-Z0-9.-]+)/g;
  let m;
  while ((m = urlRe.exec(text || ""))) {
    sources.add(m[1].replace(/^www\./, ""));
  }
  const KNOWN = ["官网", "公众号", "知乎", "CSDN", "掘金", "百度百科", "搜狗百科", "快懂百科", "头条", "抖音", "小红书", "B站", "百家号", "网易号", "搜狐号", "视频号", "微博"];
  for (const k of KNOWN) {
    if ((text || "").includes(k)) sources.add(k);
  }
  return Array.from(sources).slice(0, 8);
};

/** P0-10 官网文章被引跟踪：官网全部博客文章（与 server.py AIVIS_OWN_ARTICLES 口径一致） */
const OWN_ARTICLES: Array<{ slug: string; title: string; url: string }> = [
  { slug: "what-is-geo-generative-engine-optimization.html", title: "什么是 GEO 生成式引擎优化？定义和含义！", url: "https://www.xshetech.com/blog/what-is-geo-generative-engine-optimization.html" },
  { slug: "why-geo-vs-traditional-seo.html", title: "为什么做GEO？和传统SEO有什么区别？到底有没有用？", url: "https://www.xshetech.com/blog/why-geo-vs-traditional-seo.html" },
  { slug: "geo-service-provider-selection-guide.html", title: "国内做 GEO 的服务商有哪些？关键看这三点", url: "https://www.xshetech.com/blog/geo-service-provider-selection-guide.html" },
  { slug: "geo-dso-customer-acquisition-platform-guide.html", title: "GEO+DSO 获客平台是什么？一站式 AI 获客链路解析", url: "https://www.xshetech.com/blog/geo-dso-customer-acquisition-platform-guide.html" },
  { slug: "enterprise-ai-os-selection-guide.html", title: "企业 AI 操作系统哪个好？选型三大标准", url: "https://www.xshetech.com/blog/enterprise-ai-os-selection-guide.html" },
  { slug: "hotel-ai-system-selection-guide.html", title: "酒店 AI 系统怎么选？三条落地标准", url: "https://www.xshetech.com/blog/hotel-ai-system-selection-guide.html" },
  { slug: "hotel-kiosk-unmanned-pitfall-guide.html", title: "酒店自助机+无人化避坑指南：行业硬核揭秘", url: "https://www.xshetech.com/blog/hotel-kiosk-unmanned-pitfall-guide.html" },
  { slug: "geo-new-content-competition-ai-search.html", title: "GEO 成为新一代内容竞争焦点：AI 搜索重塑曝光规则", url: "https://www.xshetech.com/blog/geo-new-content-competition-ai-search.html" },
  { slug: "ai-search-optimization-geo-monitoring.html", title: "AI搜索优化系统是什么？GEO监测系统怎么量化AI可见度", url: "https://www.xshetech.com/blog/ai-search-optimization-geo-monitoring.html" },
  { slug: "ai-acquisition-geo-service.html", title: "AI获客怎么做？为什么要找GEO服务商", url: "https://www.xshetech.com/blog/ai-acquisition-geo-service.html" },
  { slug: "aeo-answer-engine-optimization-guide.html", title: "AEO（答案引擎优化）：定义、机制和策略基线", url: "https://www.xshetech.com/blog/aeo-answer-engine-optimization-guide.html" },
];

/** P0-10 官网文章被引跟踪：检测回答是否引用了官网具体文章（按 URL / 文件名 / 标题命中） */
const extractOwnArticles = (text: string): string[] => {
  const t = text || "";
  const hits: string[] = [];
  for (const a of OWN_ARTICLES) {
    if (t.includes(a.url) || t.includes(a.slug) || (a.title && t.includes(a.title))) hits.push(a.title);
  }
  return hits;
};

/**
 * 解析 AI 回答，判断品牌占位情况
 * - mentioned: 品牌全称/简称/产品词/创始人IP 任一出现
 * - firstPosition: 品牌词出现在回答的第一句（首个句号/换行前）
 * - top3: 品牌词出现在回答前 3 段内
 * - sources: 回答中引用的信源（URL/网站名）
 */
const parseAnswer = (answer: string, brandWords: string[], competitors: string[]): { mentioned: boolean; firstPosition: boolean; top3: boolean; competitors: string[]; sources: string[] } => {
  const text = answer || "";
  const words = brandWords.filter(Boolean);
  const hitAny = (w: string) => text.includes(w);
  const mentioned = words.some(hitAny);

  // 首条占位：第一句（首个句号/感叹号/换行前）内出现品牌词
  const firstSentence = text.split(/[。！？!?\n]/)[0] || "";
  const firstPosition = words.some((w) => firstSentence.includes(w));

  // 前3推荐：前 3 个段落内出现品牌词
  const first3Paragraphs = text.split(/\n+/).slice(0, 3).join("\n");
  const top3 = words.some((w) => first3Paragraphs.includes(w));

  // 竞品出现
  const hitComps = competitors.filter((c) => c && text.includes(c));

  // 信源溯源
  const sources = extractSources(text);

  return { mentioned, firstPosition, top3, competitors: hitComps, sources };
};

/** 聚合指标 */
const aggregateMetrics = (records: QueryRecord[]): MetricSummary => {
  const total = records.length;
  if (total === 0) {
    return {
      total: 0, mentionRate: 0, firstRate: 0, top3Rate: 0, mindShare: 0,
      sentimentPositiveRate: 0,
      byPlatform: {
        doubao: { total: 0, mentioned: 0, first: 0, top3: 0 },
        ernie: { total: 0, mentioned: 0, first: 0, top3: 0 },
        qwen: { total: 0, mentioned: 0, first: 0, top3: 0 },
        deepseek: { total: 0, mentioned: 0, first: 0, top3: 0 },
        yuanbao: { total: 0, mentioned: 0, first: 0, top3: 0 },
        kimi: { total: 0, mentioned: 0, first: 0, top3: 0 },
      },
    };
  }
  const mentioned = records.filter((r) => r.mentioned).length;
  const first = records.filter((r) => r.firstPosition).length;
  const top3 = records.filter((r) => r.top3).length;
  const positive = records.filter((r) => r.sentiment === "positive").length;
  // 心智占有率 = 0.4×提及率 + 0.35×首条占位率 + 0.25×前3推荐率
  const mindShare = Math.round((0.4 * mentioned / total + 0.35 * first / total + 0.25 * top3 / total) * 100);
  const byPlatform = {} as MetricSummary["byPlatform"];
  for (const p of AI_PLATFORMS) {
    const pr = records.filter((r) => r.ai === p.key);
    byPlatform[p.key] = {
      total: pr.length,
      mentioned: pr.filter((r) => r.mentioned).length,
      first: pr.filter((r) => r.firstPosition).length,
      top3: pr.filter((r) => r.top3).length,
    };
  }
  return {
    total, mentionRate: Math.round(mentioned / total * 100),
    firstRate: Math.round(first / total * 100),
    top3Rate: Math.round(top3 / total * 100),
    mindShare, sentimentPositiveRate: Math.round(positive / total * 100),
    byPlatform,
  };
};

/** 基于可见性数据生成策略闭环建议（GEO 完整链路第 5 步） */
const generateAdvice = (m: MetricSummary, recordCount: number): Array<{ level: "good" | "warn" | "danger"; title: string; detail: string; action: string }> => {
  const advice: Array<{ level: "good" | "warn" | "danger"; title: string; detail: string; action: string }> = [];
  if (recordCount === 0) {
    advice.push({
      level: "danger",
      title: "尚无监测数据",
      detail: "AI 可见性监测尚未开始，无法判断 GEO 是否奏效。",
      action: "点击「新增查询记录」，把关键问题问到豆包/文心/通义/DeepSeek 后粘贴回答回来，建立基线数据。",
    });
    return advice;
  }
  if (m.mentionRate < 30) {
    advice.push({
      level: "danger",
      title: "品牌未进入模型候选池",
      detail: `提及率仅 ${m.mentionRate}%，品牌词在 AI 回答中极少出现，说明内容尚未被模型索引/引用。`,
      action: "优先补强：信源入库（官网/公众号/蓝V 权威信源）→ 在主流平台发布结构化内容 → 检查品牌词是否出现在候选池。",
    });
  } else if (m.mentionRate >= 30 && m.firstRate < 40) {
    advice.push({
      level: "warn",
      title: "品牌有提及但占位靠后",
      detail: `提及率 ${m.mentionRate}% 但首条占位率仅 ${m.firstRate}%，品牌多出现在答案中后段。`,
      action: "优化内容首段：把品牌定位/核心卖点写成可独立摘取的第一句知识块，提升首条占位概率。",
    });
  } else {
    advice.push({
      level: "good",
      title: "品牌占位良好",
      detail: `提及率 ${m.mentionRate}% + 首条占位率 ${m.firstRate}%，AI 可见性已初步建立。`,
      action: "保持定期监测节奏；若业务转化未同步提升，问题可能出在转化环节（落地页/线索承接）而非 GEO，需单独排查。",
    });
  }
  if (m.mindShare >= 50) {
    advice.push({
      level: "good",
      title: "心智占有率领先",
      detail: `综合心智占有率 ${m.mindShare}%（0.4×提及率+0.35×首条+0.25×前3），品牌在 AI 认知中占优。`,
      action: "可将 SEM 预算逐步向 GEO 倾斜，将节省的广告费用于内容矩阵扩张。",
    });
  } else if (m.mindShare >= 25) {
    advice.push({
      level: "warn",
      title: "心智占有率中等",
      detail: `综合心智占有率 ${m.mindShare}%，品牌被 AI 推荐的概率处于中等水平。`,
      action: "在 GEO 基础打牢前，SEM 投放不要停：SEM 短期起量，GEO 构建长期 AI 资产，两者互补而非替代。",
    });
  } else {
    advice.push({
      level: "warn",
      title: "心智占有率偏低",
      detail: `综合心智占有率 ${m.mindShare}%，竞品在 AI 认知中占优。`,
      action: "补打竞品对标词内容 + 抢占首条占位：重点优化「品牌词+品类词」组合的知识块。",
    });
  }
  // 竞品维度提醒（并入策略建议第 4 条）
  advice.push({
    level: "warn",
    title: "四维度对照",
    detail: "业务层（线索转化）指标请到经营中心/增长指标查看；本监测中心负责 AI 可见性这一核心维度。",
    action: "建议每周固定时间批量执行一轮 4 模型 × 5 问题的查询，形成稳定时间序列后观察趋势拐点。",
  });
  return advice;
};

// ============================================
// 子组件：KPI 卡片
// ============================================

function KpiCard({ label, value, sub, color, icon: Icon }: { label: string; value: string; sub: string; color: string; icon: typeof Eye }) {
  return (
    <div className="rounded-lg border p-2.5" style={{ borderColor: `${color}22`, background: `${color}08` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} style={{ color }} />
        <span className="text-[9px] font-mono text-gray-500">{label}</span>
      </div>
      <div className="text-[18px] font-bold font-mono" style={{ color }}>{value}</div>
      <div className="text-[8px] font-mono text-gray-600 mt-0.5">{sub}</div>
    </div>
  );
}

// ============================================
// 主组件
// ============================================

export default function AiVisibilityMonitor() {
  const [state, setState] = useState<VisibilityState>(EMPTY_STATE);
  const [competitors, setCompetitors] = useState<string[]>(DEFAULT_COMPETITORS);
  const [brandWords, setBrandWords] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"query" | "trend" | "compete" | "advice" | "feedback" | "settings">("query");

  // 查询录入表单
  const [questionId, setQuestionId] = useState<string>("");
  const [customQuestion, setCustomQuestion] = useState("");
  const [ai, setAi] = useState<AiPlatform>("doubao");
  const [answer, setAnswer] = useState("");
  const [parsing, setParsing] = useState(false);

  // 问题库新增
  const [newQuestion, setNewQuestion] = useState("");

  // ============ v6.1.12 AI 自动诊断（输入行业 + 勾选平台 → 模拟提问解析占位） ============
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagIndustry, setDiagIndustry] = useState("");
  const [diagPlatforms, setDiagPlatforms] = useState<AiPlatform[]>(["doubao", "ernie", "yuanbao", "kimi", "deepseek", "qwen"]);
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagProgress, setDiagProgress] = useState("");
  const [diagResults, setDiagResults] = useState<DiagnosisItem[]>([]);

  // ============ P0-新增 反哺闭环：0提及泛词 → 内容选题 + 信源铺设 ============
  const [gapTopics, setGapTopics] = useState<GapTopic[]>([]);
  const [gapGenerating, setGapGenerating] = useState<string>("");
  const [gapCustom, setGapCustom] = useState("");

  // ============ P0-7 服务端自动轮询 ============
  const [aivisEnabled, setAivisEnabled] = useState(false);
  const [aivisInterval, setAivisInterval] = useState(24);
  const [aivisLastRun, setAivisLastRun] = useState("");
  const [aivisLatest, setAivisLatest] = useState<{ date?: string; mentionRate?: number; firstRate?: number; top3Rate?: number; ok?: number; total?: number } | null>(null);
  const [pollRunning, setPollRunning] = useState(false);

  // ============ P0-8 复盘看板：趋势窗口（14天/4周/8周/12周）+ 服务端历史 + 信源质量评分 ============
  const [trendWindow, setTrendWindow] = useState<number>(14);
  const [serverTrend, setServerTrend] = useState<Array<{ date: string; mentionRate: number; firstRate: number; top3Rate: number; total: number }>>([]);
  const [serverSourceQuality, setServerSourceQuality] = useState<{
    score: number; tier: string; total: number; coverage: number;
    tiers: Record<string, number>; top: Array<{ name: string; weight: number }>;
  } | null>(null);
  /** P0-10 官网文章被引跟踪：服务端历史轮询中各篇官网文章被 AI 引用次数 */
  const [serverOwnArticleStats, setServerOwnArticleStats] = useState<Array<{ title: string; count: number }>>([]);

  useEffect(() => {
    fetch(`/ai-visibility/history?days=${trendWindow}`).then((r) => r.json()).then((d) => {
      if (d?.success) {
        setServerTrend(d.trend || []);
        setServerSourceQuality(d.sourceQuality || null);
        setServerOwnArticleStats(d.ownArticleStats || []);
      }
    }).catch(() => { /* 忽略 */ });
  }, [trendWindow]);

  useEffect(() => {
    fetch("/ai-visibility/status").then((r) => r.json()).then((d) => {
      if (d?.success) {
        setAivisEnabled(!!d.config?.enabled);
        setAivisInterval(d.config?.intervalHours || 24);
        setAivisLastRun(d.config?.lastRun || "");
        setAivisLatest(d.latest || null);
      }
    }).catch(() => { /* ignore */ });
  }, []);

  const toggleAutoPoll = () => {
    const next = !aivisEnabled;
    setAivisEnabled(next);
    fetch("/ai-visibility/config", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: next, intervalHours: aivisInterval }),
    }).then((r) => r.json()).then((d) => { if (d?.success) setAivisLastRun(d.config?.lastRun || ""); }).catch(() => { /* ignore */ });
  };

  const runPollNow = async () => {
    setPollRunning(true);
    try {
      const r = await fetch("/ai-visibility/poll", { method: "POST" });
      const d = await r.json();
      if (d?.success) {
        setAivisLatest(d.summary);
        setAivisLastRun(new Date().toISOString().replace("T", " ").slice(0, 19));
      } else {
        alert(d?.error || "轮询失败");
      }
    } catch (e) {
      alert("轮询请求失败：" + (e instanceof Error ? e.message : String(e)));
    }
    setPollRunning(false);
  };

  // P0-7 调整自动轮询间隔（小时），保存到服务端 auto_poll.json
  const changeInterval = (hours: number) => {
    setAivisInterval(hours);
    fetch("/ai-visibility/config", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: aivisEnabled, intervalHours: hours }),
    }).then((r) => r.json()).then((d) => { if (d?.success) setAivisLastRun(d.config?.lastRun || ""); }).catch(() => { /* ignore */ });
  };

  // 初始化
  useEffect(() => {
    setState(loadState());
    setCompetitors(loadCompetitors());
    setGapTopics(loadGaps());
    const bw = loadBrandWords();
    setBrandWords([bw.brandFullName, bw.brandShortName, ...bw.productNames, ...bw.founderIpNames].filter(Boolean));
  }, []);

  // 持久化
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* 忽略 */ }
  }, [state]);

  useEffect(() => {
    try { localStorage.setItem(GAP_STORAGE_KEY, JSON.stringify(gapTopics)); } catch { /* 忽略 */ }
  }, [gapTopics]);

  useEffect(() => {
    try { localStorage.setItem(COMPETITOR_KEY, JSON.stringify(competitors)); } catch { /* 忽略 */ }
  }, [competitors]);

  // 聚合指标
  const metrics = useMemo(() => aggregateMetrics(state.records), [state.records]);
  const advice = useMemo(() => generateAdvice(metrics, state.records.length), [metrics, state.records.length]);

  // P0-8 按日期聚合（提及率/首条率趋势）
  const trendByDate = useMemo(() => {
    const buckets: Record<string, { total: number; mentioned: number; first: number }> = {};
    for (const r of state.records) {
      const day = (r.checkedAt || "").slice(0, 10);
      if (!day) continue;
      if (!buckets[day]) buckets[day] = { total: 0, mentioned: 0, first: 0 };
      buckets[day].total++;
      if (r.mentioned) buckets[day].mentioned++;
      if (r.firstPosition) buckets[day].first++;
    }
    return Object.entries(buckets)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([day, v]) => ({
        date: day,
        mentionRate: v.total ? Math.round((v.mentioned / v.total) * 100) : 0,
        firstRate: v.total ? Math.round((v.first / v.total) * 100) : 0,
        total: v.total,
      }));
  }, [state.records]);

  // P0-9 信源溯源：统计所有记录中被 AI 回答引用的信源（URL 域名 / 网站名）出现频次
  const sourceStats = useMemo(() => {
    const cnt: Record<string, number> = {};
    for (const r of state.records) {
      for (const s of r.sources || []) cnt[s] = (cnt[s] || 0) + 1;
    }
    return Object.entries(cnt).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [state.records]);

  // P0-9 信源铺设指引：按权威度分级，依据「AI 实际引用过哪些信源」反推内容还缺哪些阵地
  const SOURCE_CHANNELS: Array<{ label: string; tier: "A" | "B" | "C"; keywords: string[]; action: string }> = [
    { label: "官网/产品页", tier: "A", keywords: ["xshetech", "官网", "site"], action: "产品页/白皮书结构化 + schema 部署" },
    { label: "公众号", tier: "A", keywords: ["公众号", "weixin", "mp.weixin"], action: "统一术语与口径的专题稿" },
    { label: "百科词条", tier: "A", keywords: ["百科"], action: "权威词条背书（百度/搜狗/快懂）" },
    { label: "知乎", tier: "A", keywords: ["知乎", "zhihu"], action: "高质量问答与深度内容" },
    { label: "CSDN/掘金", tier: "B", keywords: ["csdn", "掘金", "juejin"], action: "系统架构/技术拆解文章" },
    { label: "百家号", tier: "B", keywords: ["百家号", "baijiahao"], action: "百度生态分发包" },
    { label: "社媒(抖音/小红书/B站/视频号/微博)", tier: "C", keywords: ["抖音", "小红书", "b站", "bilibili", "视频号", "微博", "weibo"], action: "短视频/图文声量" },
    { label: "新闻源(头条/网易/搜狐号)", tier: "C", keywords: ["头条", "网易号", "搜狐号", "今日头条"], action: "新闻稿/行业稿分发" },
  ];
  const sourceCoverage = useMemo(() => {
    const covered = new Set<string>();
    const cited: Array<{ name: string; count: number }> = [];
    for (const [name, count] of sourceStats) {
      const n = name.toLowerCase();
      let hit = false;
      for (const ch of SOURCE_CHANNELS) {
        if (ch.keywords.some((k) => n.includes(k))) {
          covered.add(ch.label);
          hit = true;
        }
      }
      if (hit) cited.push({ name, count });
    }
    const gaps = SOURCE_CHANNELS.filter((c) => !covered.has(c.label))
      .sort((a, b) => a.tier.localeCompare(b.tier));
    return { covered: [...covered], gaps, cited };
  }, [sourceStats]);

  // P0-8 趋势数据：优先用服务端历史（自动轮询每日落库），无服务端数据时回退本地录入记录
  const trendData = useMemo(() => {
    if (serverTrend.length > 0) return serverTrend;
    return trendByDate;
  }, [serverTrend, trendByDate]);

  // ============ 问题库管理 ============
  const handleAddQuestion = () => {
    const q = newQuestion.trim();
    if (!q) return;
    setState((s) => ({ ...s, questions: [...s.questions, { id: genId(), question: q, auto: false }] }));
    setNewQuestion("");
  };

  const handleDeleteQuestion = (id: string) => {
    setState((s) => ({ ...s, questions: s.questions.filter((q) => q.id !== id) }));
  };

  // P0-6 加载固定标准清单到问题库（去重）
  const loadPresetQuestions = () => {
    setState((s) => {
      const existing = new Set(s.questions.map((q) => q.question));
      const toAdd = PRESET_QUESTIONS
        .filter((q) => !existing.has(q))
        .map((q) => ({ id: `preset-${genId()}`, question: q, auto: false }));
      if (toAdd.length === 0) return s;
      return { ...s, questions: [...s.questions, ...toAdd] };
    });
  };

  // 自动同步品牌词问题
  const syncAutoQuestions = useCallback(() => {
    setState((s) => {
      const autoQs: VisibilityQuestion[] = [];
      for (const w of brandWords) {
        if (w.length < 2) continue;
        autoQs.push({ id: `auto-${w}`, question: `${w} 是什么？有什么优势？`, auto: true, keyword: w });
        autoQs.push({ id: `auto-${w}-rec`, question: `推荐一下 ${w} 相关的工具/服务？`, auto: true, keyword: w });
      }
      const manual = s.questions.filter((q) => !q.auto);
      return { ...s, questions: [...manual, ...autoQs].slice(0, 40) };
    });
  }, [brandWords]);

  useEffect(() => { syncAutoQuestions(); }, [syncAutoQuestions]);

  // ============ 查询记录 ============
  const handleSubmitQuery = () => {
    // 确定问题文本
    const qObj = state.questions.find((q) => q.id === questionId);
    const questionText = customQuestion.trim() || qObj?.question || "";
    if (!questionText || !answer.trim()) return;
    setParsing(true);
    // 延迟模拟解析（纯前端规则，非 LLM）
    setTimeout(() => {
      const parsed = parseAnswer(answer, brandWords, competitors);
      const record: QueryRecord = {
        id: genId(),
        questionId,
        question: questionText,
        ai,
        answer: answer.trim(),
        mentioned: parsed.mentioned,
        firstPosition: parsed.firstPosition,
        top3: parsed.top3,
        sentiment: judgeSentiment(answer),
        competitors: parsed.competitors,
        sources: parsed.sources,
        ownArticles: extractOwnArticles(answer),
        checkedAt: new Date().toISOString(),
      };
      setState((s) => ({ ...s, records: [record, ...s.records] }));
      setAnswer("");
      setCustomQuestion("");
      setParsing(false);
    }, 350);
  };

  const handleDeleteRecord = (id: string) => {
    setState((s) => ({ ...s, records: s.records.filter((r) => r.id !== id) }));
  };

  // ============ v6.1.12 AI 自动诊断：输入行业 + 勾选平台 → 模拟各 AI 平台回答并解析占位 ============
  // P0-6: mode='preset' 时使用固定标准清单跑全套
  const runDiagnosis = async (mode?: "auto" | "preset") => {
    const questions = mode === "preset" ? PRESET_QUESTIONS : buildDiagQuestions(diagIndustry, brandWords);
    const platforms = diagPlatforms;
    if (platforms.length === 0) return;
    setDiagRunning(true);
    setDiagResults([]);

    // 获取 DeepSeek API Key（模拟引擎）：apiConfig → env
    let apiKey = apiConfig.getApiKey('deepseek');
    if (!apiKey) {
      try {
        const env = (import.meta as any).env;
        if (env?.VITE_DEEPSEEK_API_KEY) apiKey = env.VITE_DEEPSEEK_API_KEY;
      } catch { /* ignore */ }
    }
    if (!apiKey) {
      setDiagProgress("未配置 DeepSeek API Key（设置页或 .env），无法模拟诊断");
      setDiagRunning(false);
      return;
    }

    const results: DiagnosisItem[] = [];
    let total = platforms.length * questions.length;
    let done = 0;
    for (const p of platforms) {
      for (const q of questions) {
        setDiagProgress(`${AI_PLATFORMS.find((x) => x.key === p)?.name} · ${q.slice(0, 18)}…`);
        try {
          let content = '';
          const realEndpoint = REAL_API_ENDPOINT[p];
          if (realEndpoint) {
            // P1+ 真实 API（豆包/混元，服务端代理，key 不暴露）
            try {
              const r = await fetch(realEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  messages: [
                    { role: 'system', content: DIAG_SYSTEM_PROMPT[p] },
                    { role: 'user', content: q },
                  ],
                }),
              });
              const d = await r.json();
              if (d?.success && d.content) {
                content = d.content;
              }
            } catch { /* 真实 API 失败，落到 DeepSeek 模拟 */ }
          }
          // 无真实结果 → 用 DeepSeek 模拟该平台风格（非真实抓取）
          if (!content) {
            const resp = await fetch('/deepseek-api/v1/chat/completions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
              body: JSON.stringify({
                model: 'deepseek-chat',
                messages: [
                  { role: 'system', content: DIAG_SYSTEM_PROMPT[p] },
                  { role: 'user', content: q },
                ],
                temperature: 0.4,
                max_tokens: 900,
                stream: false,
              }),
            });
            if (!resp.ok) {
              results.push({ platform: p, question: q, answer: '', mentioned: false, firstPosition: false, top3: false, sentiment: 'neutral', error: `HTTP ${resp.status}` });
              done++;
              continue;
            }
            const data = await resp.json();
            // 兼容两种响应格式：vite 直达 OpenAI 格式 {choices} / 生产 server.py 代理 {success,content}
            content = data?.choices?.[0]?.message?.content || data?.content || '';
          }
          const parsed = parseAnswer(content, brandWords, competitors);
          results.push({
            platform: p,
            question: q,
            answer: content,
            mentioned: parsed.mentioned,
            firstPosition: parsed.firstPosition,
            top3: parsed.top3,
            sentiment: judgeSentiment(content),
          });
        } catch (err: any) {
          results.push({ platform: p, question: q, answer: '', mentioned: false, firstPosition: false, top3: false, sentiment: 'neutral', error: err?.message || String(err) });
        }
        done++;
        setDiagProgress(`已完成 ${done}/${total}`);
      }
    }
    setDiagResults(results);
    // v6.3.1 自动写入持久化查询记录：一键诊断结果进入指标聚合/时间序列
    //（此前只存内存 diagResults → 刷新即丢，且不参与提及率/首条/前3 统计）
    setState((s) => {
      const newRecords: QueryRecord[] = results
        .filter((r) => !r.error && r.answer.trim().length > 0)
        .map((r) => ({
          id: `diag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          questionId: `diag-${r.question}`,
          question: r.question,
          ai: r.platform,
          answer: r.answer,
          mentioned: r.mentioned,
          firstPosition: r.firstPosition,
          top3: r.top3,
          sentiment: r.sentiment,
          competitors: [],
          sources: extractSources(r.answer),
          ownArticles: extractOwnArticles(r.answer),
          checkedAt: new Date().toISOString(),
        }));
      if (newRecords.length === 0) return s;
      return { ...s, records: [...newRecords, ...s.records].slice(0, 200) };
    });
    // ============ P0-新增 反哺闭环：0 提及泛词自动转为内容选题 ============
    // 全平台都未提及品牌的问题 = 内容缺口 → 进入"反哺闭环"标签页，可一键生成内容并跟踪信源铺设
    const gapMap = new Map<string, string[]>();
    for (const r of results) {
      if (!r.error && r.answer.trim().length > 0 && !r.mentioned) {
        const arr = gapMap.get(r.question) || [];
        arr.push(r.platform);
        gapMap.set(r.question, arr);
      }
    }
    if (gapMap.size > 0) {
      const now = new Date().toISOString();
      setGapTopics((prev) => {
        const next = [...prev];
        for (const [question, platforms] of gapMap) {
          const existing = next.find((g) => g.question === question);
          if (existing) {
            // 已有选题：补充平台信息，不重复
            const merged = Array.from(new Set([...existing.platforms, ...platforms]));
            if (merged.length !== existing.platforms.length) {
              existing.platforms = merged;
              existing.updatedAt = now;
            }
          } else {
            next.unshift({ id: genId(), question, platforms, content: "", publishedSources: [], createdAt: now, updatedAt: now });
          }
        }
        return next.slice(0, 100);
      });
    }
    setDiagRunning(false);
    setDiagProgress("");
  };

  const diagSummary = useMemo(() => {
    const ok = diagResults.filter((r) => !r.error);
    if (ok.length === 0) return { total: 0, mentionRate: 0, firstRate: 0, top3Rate: 0, byPlatform: {} as Record<string, { total: number; mention: number }> };
    const byPlatform: Record<string, { total: number; mention: number }> = {};
    for (const p of AI_PLATFORMS) {
      const pr = ok.filter((r) => r.platform === p.key);
      byPlatform[p.key] = { total: pr.length, mention: pr.filter((r) => r.mentioned).length };
    }
    return {
      total: ok.length,
      mentionRate: Math.round((ok.filter((r) => r.mentioned).length / ok.length) * 100),
      firstRate: Math.round((ok.filter((r) => r.firstPosition).length / ok.length) * 100),
      top3Rate: Math.round((ok.filter((r) => r.top3).length / ok.length) * 100),
      byPlatform,
    };
  }, [diagResults]);

  // ============ GEO 基线测试（v5.2 审计补齐） ============
  // 模板"｛城市｝｛行业｝哪家靠谱"：从 GeoInputEnhanced 的 xeaos_geo_input 读取地域+业务关键词，
  // 自动生成基线问题加入问题库（对应白皮书第十八章⑨ GEO 基线测试）
  const [baselineCount, setBaselineCount] = useState(0);

  const generateBaselineQuestions = () => {
    try {
      const raw = localStorage.getItem("xeaos_geo_input");
      let regions: string[] = [];
      let industryWords: string[] = [];
      if (raw) {
        const parsed = JSON.parse(raw);
        const locations: any[] = Array.isArray(parsed?.geoLocations) ? parsed.geoLocations : [];
        regions = locations.map((l) => l?.region).filter((r) => typeof r === "string" && r.length > 0);
        const keywords: any[] = Array.isArray(parsed?.keywords) ? parsed.keywords : [];
        industryWords = keywords
          .filter((k) => k?.tier === "business_precise" || k?.tier === "industry_longtail")
          .map((k) => k?.keyword)
          .filter((k) => typeof k === "string" && k.length >= 2)
          .slice(0, 6);
      }
      if (regions.length === 0) regions = ["长沙"];
      if (industryWords.length === 0) industryWords = ["AI 获客", "内容营销"];
      const templates = ["{region}{industry}哪家靠谱", "{region}做{industry}哪家好", "{region}{industry}哪家值得推荐"];
      const questions: string[] = [];
      for (const r of regions.slice(0, 3)) {
        for (const ind of industryWords.slice(0, 4)) {
          for (const t of templates) {
            questions.push(t.replace("{region}", r).replace("{industry}", ind));
          }
        }
      }
      // 去重 + 限制 12 条，加入问题库（标记手动，可删除）
      const uniq = Array.from(new Set(questions)).slice(0, 12);
      setState((s) => {
        const existing = new Set(s.questions.map((q) => q.question));
        const fresh = uniq.filter((q) => !existing.has(q)).map((q) => ({ id: genId(), question: q, auto: false }));
        return { ...s, questions: [...s.questions, ...fresh] };
      });
      setBaselineCount((c) => c + uniq.length);
    } catch (err) {
      console.warn("[AiVisibilityMonitor] 生成基线问题失败:", err);
    }
  };

  // ============ P0-新增 反哺闭环操作 ============
  // 手动录入一条泛词选题（如运营发现的"长沙企业AI获客哪家好"）
  const addGapManual = () => {
    const q = gapCustom.trim();
    if (!q) return;
    const now = new Date().toISOString();
    setGapTopics((prev) => {
      if (prev.some((g) => g.question === q)) return prev;
      return [{ id: genId(), question: q, platforms: [], content: "", publishedSources: [], createdAt: now, updatedAt: now }, ...prev];
    });
    setGapCustom("");
  };

  // 用 DeepSeek 为泛词选题生成"可被 AI 引用"的事实型内容（含品牌事实，不点名竞品）
  const generateGapContent = async (g: GapTopic) => {
    let apiKey = apiConfig.getApiKey('deepseek');
    if (!apiKey) {
      try {
        const env = (import.meta as any).env;
        if (env?.VITE_DEEPSEEK_API_KEY) apiKey = env.VITE_DEEPSEEK_API_KEY;
      } catch { /* ignore */ }
    }
    if (!apiKey) {
      alert("未配置 DeepSeek API Key（设置页或 .env），无法生成内容");
      return;
    }
    setGapGenerating(g.id);
    try {
      const bw = loadBrandWords();
      const brandFacts = [
        `公司主体：${bw.brandFullName || '湖南小舍科技有限公司'}`,
        `中文品牌：${bw.brandShortName || '小舍AI'}`,
        `产品：XEAOS 系统 / XEAOS GEO+DSO 全域 AI 获客平台`,
        `定位：企业 AI 操作系统，覆盖企业中心/产品中心/营销中心/CRM/指挥中心/风控中心/预测中心/基金中心等`,
        `官网：https://www.xshetech.com`,
      ].join('；');
      const sys = '你是一位 GEO（生成式引擎优化）内容专家。写一篇可被 AI 搜索引擎引用的科普/实操文章。要求：1) 客观、专业、有数据和步骤，不写广告腔；2) 以"湖南小舍科技（小舍AI）"为事实来源自然提及，不点名竞品；3) 直接输出正文（600-900字），标题用 # 开头，分小标题，含 FAQ 式结尾。';
      const user = `请围绕这个问题写一篇文章：${g.question}\n\n可用品牌事实（只能使用以下事实，不要虚构）：${brandFacts}`;
      const resp = await fetch('/deepseek-api/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            { role: 'system', content: sys },
            { role: 'user', content: user },
          ],
          temperature: 0.6,
          max_tokens: 1600,
          stream: false,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const content = data?.choices?.[0]?.message?.content || data?.content || '';
      if (!content) throw new Error("DeepSeek 返回空内容");
      setGapTopics((prev) => prev.map((x) => (x.id === g.id ? { ...x, content, updatedAt: new Date().toISOString() } : x)));
    } catch (e: any) {
      alert("生成失败：" + (e?.message || String(e)));
    }
    setGapGenerating("");
  };

  // 标记某信源已铺设（发布过），用于跟踪"内容有没有铺到能被 AI 引用的地方"
  const toggleGapSource = (id: string, src: string) => {
    setGapTopics((prev) =>
      prev.map((x) => {
        if (x.id !== id) return x;
        const has = x.publishedSources.includes(src);
        return { ...x, publishedSources: has ? x.publishedSources.filter((s) => s !== src) : [...x.publishedSources, src], updatedAt: new Date().toISOString() };
      })
    );
  };

  const deleteGap = (id: string) => setGapTopics((prev) => prev.filter((g) => g.id !== id));

  // 反哺闭环汇总
  const gapSummary = useMemo(() => {
    const total = gapTopics.length;
    const hasContent = gapTopics.filter((g) => g.content).length;
    const published = gapTopics.filter((g) => g.publishedSources.length > 0).length;
    const ready = gapTopics.filter((g) => g.content && g.publishedSources.length === 0).length;
    return { total, hasContent, published, ready };
  }, [gapTopics]);

  // 最近趋势：按时间排序取最近 10 次，逐条显示提及/首条/前3
  const recentRecords = useMemo(() => {
    return [...state.records].sort((a, b) => (a.checkedAt < b.checkedAt ? 1 : -1)).slice(0, 10);
  }, [state.records]);

  // 平台维度表现
  const platformStats = AI_PLATFORMS.map((p) => ({
    ...p,
    stat: metrics.byPlatform[p.key],
  }));

  // 竞品出现统计（全部记录中）
  const competitorStats = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of state.records) {
      for (const c of r.competitors) {
        map.set(c, (map.get(c) || 0) + 1);
      }
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count, rate: state.records.length > 0 ? Math.round(count / state.records.length * 100) : 0 }))
      .sort((a, b) => b.count - a.count);
  }, [state.records]);

  const sentimentMeta: Record<string, { label: string; color: string }> = {
    positive: { label: "正面", color: "#10B981" },
    neutral: { label: "中性", color: "#9CA3AF" },
    negative: { label: "负面", color: "#EF4444" },
  };

  // 渲染：解析结果标签
  const renderBadge = (ok: boolean, text: string, okColor = "#10B981", badColor = "#6B7280") => (
    <span
      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono"
      style={{ background: `${ok ? okColor : badColor}15`, color: ok ? okColor : badColor }}
    >
      {ok ? <CheckCircle2 size={7} /> : <XCircle size={7} />}
      {text}
    </span>
  );

  return (
    <div className="h-full overflow-auto p-3 space-y-3">
      {/* ============ 头部说明 ============ */}
      <div className="flex items-start gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03] p-2.5">
        <Radar size={13} className="text-cyan-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[11px] font-medium text-gray-300">AI 可见性监测中心 —— GEO 效果衡量的核心环节</p>
          <p className="text-[9px] font-mono text-gray-600 leading-relaxed">
            主动拿关键问题定期去问豆包 / 文心一言 / 通义千问 / DeepSeek，把回答粘贴回来即可自动解析占位情况。
            这是判断「GEO 本身有没有奏效」的直接证据，别等数据被动回流。
          </p>
        </div>
        <button
          onClick={() => setDiagOpen(!diagOpen)}
          className={`ml-auto flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition-colors flex-shrink-0 ${
            diagOpen ? "bg-purple-500/25 text-purple-300 border border-purple-500/30" : "bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 border border-purple-500/20"
          }`}
          title="输入行业 → 勾选 AI 平台 → 系统自动模拟各平台回答并解析品牌占位情况（基线参考）"
        >
          <Zap size={10} />
          一键诊断
        </button>
      </div>

      {/* ============ v6.1.12 一键诊断面板 ============ */}
      {diagOpen && (
        <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.03] p-2.5 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">行业/关键词</span>
            <input
              value={diagIndustry}
              onChange={(e) => setDiagIndustry(e.target.value)}
              placeholder="如：企业 AI 操作系统 / 酒店数字化 / 装修公司"
              className="flex-1 min-w-[180px] px-2 py-1 text-[10px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-purple-500/40 placeholder-gray-600 text-gray-300"
            />
            <span className="text-[10px] font-mono text-gray-500">勾选 AI 平台：</span>
            {AI_PLATFORMS.map((p) => {
              const on = diagPlatforms.includes(p.key);
              return (
                <button
                  key={p.key}
                  onClick={() => setDiagPlatforms((prev) => on ? prev.filter((x) => x !== p.key) : [...prev, p.key])}
                  className={`px-2 py-0.5 rounded text-[9px] font-mono border transition-colors ${on ? "text-white" : "text-gray-500 hover:text-gray-300 border-white/[0.06]"}`}
                  style={on ? { background: `${p.color}22`, borderColor: `${p.color}55`, color: p.color } : {}}
                  title={`${p.name}${on ? "已勾选（再点取消）" : "未勾选（点击加入多选）"} — 可同时勾选多个平台`}
                >
                  {on ? "✓ " : ""}{p.name}
                </button>
              );
            })}
            <button
              onClick={() => runDiagnosis("preset")}
              disabled={diagRunning || diagPlatforms.length === 0}
              className="flex items-center gap-1 px-3 py-1 rounded text-[10px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 border border-cyan-500/40 transition-colors disabled:opacity-40"
              title="用固定标准清单（8 条 GEO 可见性问题）对勾选平台跑全套"
            >
              {diagRunning ? <Loader2 size={10} className="animate-spin" /> : <Radar size={10} />}
              一键跑全套
            </button>
            <button
              onClick={() => runDiagnosis()}
              disabled={diagRunning || diagPlatforms.length === 0}
              className="flex items-center gap-1 px-3 py-1 rounded text-[10px] font-mono text-white bg-purple-500/25 hover:bg-purple-500/35 border border-purple-500/40 transition-colors disabled:opacity-40"
            >
              {diagRunning ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
              {diagRunning ? "诊断中…" : "开始诊断"}
            </button>
          </div>
          <p className="text-[8px] font-mono text-gray-600 leading-relaxed">
            🤖 诊断说明：豆包/文心/混元/Kimi/DeepSeek/通义 全部走真实 API（服务端代理，key 不暴露），自动解析品牌是否被提及、是否占首条/前3。
            单平台失败时自动降级 DeepSeek 兜底；真实排名建议再到各平台亲自提问，把回答粘贴到「查询记录」复核。
          </p>

          {diagProgress && (
            <div className="flex items-center gap-1.5 text-[9px] font-mono text-purple-300">
              <Loader2 size={9} className="animate-spin" />
              {diagProgress}
            </div>
          )}

          {diagResults.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-[10px] font-mono">
                <span className="text-gray-400">诊断汇总：</span>
                <span className="text-blue-400">提及率 {diagSummary.mentionRate}%</span>
                <span className="text-emerald-400">首条 {diagSummary.firstRate}%</span>
                <span className="text-amber-400">前3 {diagSummary.top3Rate}%</span>
                <span className="text-gray-600">（{diagSummary.total} 次模拟查询）</span>
              </div>
              <div className="space-y-1 max-h-[260px] overflow-y-auto">
                {diagResults.map((r, i) => {
                  const p = AI_PLATFORMS.find((x) => x.key === r.platform);
                  return (
                    <div key={i} className="rounded border border-white/[0.05] bg-white/[0.01] px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-mono" style={{ color: p?.color }}>{p?.name}</span>
                        <span className="text-[9px] text-gray-500 truncate flex-1">{r.question}</span>
                        {r.error ? (
                          <span className="text-[8px] font-mono text-red-400">失败: {r.error}</span>
                        ) : (
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {r.mentioned ? <CheckCircle2 size={10} className="text-emerald-400" /> : <XCircle size={10} className="text-red-400" />}
                            <span className={`text-[9px] font-mono ${r.mentioned ? "text-emerald-400" : "text-red-400"}`}>{r.mentioned ? "提及" : "未提及"}</span>
                            {r.firstPosition && <span className="text-[8px] font-mono text-emerald-400">· 首条</span>}
                            {!r.firstPosition && r.top3 && <span className="text-[8px] font-mono text-amber-400">· 前3</span>}
                          </span>
                        )}
                      </div>
                      {r.answer && (
                        <details className="mt-1">
                          <summary className="text-[8px] font-mono text-gray-600 cursor-pointer hover:text-gray-400">查看模拟回答（{r.answer.length}字）</summary>
                          <pre className="mt-1 text-[9px] text-gray-500 whitespace-pre-wrap break-words leading-relaxed max-h-[120px] overflow-y-auto">{r.answer}</pre>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ KPI 总览 ============ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KpiCard label="提及率" value={metrics.total > 0 ? `${metrics.mentionRate}%` : "--"} sub={`${metrics.total} 次查询`} color="#3B82F6" icon={Eye} />
        <KpiCard label="首条占位率" value={metrics.total > 0 ? `${metrics.firstRate}%` : "--"} sub="品牌出现在答案首位" color="#10B981" icon={Target} />
        <KpiCard label="前3推荐率" value={metrics.total > 0 ? `${metrics.top3Rate}%` : "--"} sub="品牌进入前3推荐" color="#F59E0B" icon={TrendingUp} />
        <KpiCard label="心智占有率" value={metrics.total > 0 ? `${metrics.mindShare}%` : "--"} sub="0.4提及+0.35首条+0.25前3" color="#A855F7" icon={Sparkles} />
      </div>

      {/* ============ Tabs ============ */}
      <div className="flex items-center gap-1 flex-wrap">
        {([
          { id: "query" as const, label: "查询记录", icon: ClipboardPaste, color: "#3B82F6" },
          { id: "trend" as const, label: "趋势分析", icon: BarChart3, color: "#10B981" },
          { id: "compete" as const, label: "竞品对比", icon: Swords, color: "#F59E0B" },
          { id: "advice" as const, label: "策略建议", icon: Lightbulb, color: "#A855F7" },
          { id: "feedback" as const, label: "反哺闭环", icon: RefreshCw, color: "#10B981" },
          { id: "settings" as const, label: "问题库/设置", icon: Settings2, color: "#6B7280" },
        ]).map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono transition ${
                active ? "text-white bg-white/[0.06] border border-white/[0.1]" : "text-gray-500 hover:text-gray-300 border border-transparent"
              }`}
              style={active ? { color: tab.color } : {}}
            >
              <Icon size={10} />
              {tab.label}
              {tab.id === "query" && state.records.length > 0 && (
                <span className="text-[8px] opacity-60">({state.records.length})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ============ Tab: 查询记录 ============ */}
      {activeTab === "query" && (
        <div className="space-y-3">
          {/* 录入表单 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
            <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
              <ClipboardPaste size={10} className="text-cyan-400" />
              新增主动查询记录（把 AI 的真实回答粘贴进来）
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* 问题选择 */}
              <div>
                <div className="text-[8px] font-mono text-gray-600 mb-1">① 选择/输入关键问题</div>
                <select
                  value={questionId}
                  onChange={(e) => setQuestionId(e.target.value)}
                  className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40"
                >
                  <option value="">-- 从问题库选择 --</option>
                  {state.questions.map((q) => (
                    <option key={q.id} value={q.id}>{q.question}</option>
                  ))}
                </select>
                <input
                  value={customQuestion}
                  onChange={(e) => setCustomQuestion(e.target.value)}
                  placeholder="或自定义问题..."
                  className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40"
                />
              </div>
              {/* AI 平台选择 */}
              <div>
                <div className="text-[8px] font-mono text-gray-600 mb-1">② 选择 AI 平台</div>
                <div className="flex gap-1.5">
                  {AI_PLATFORMS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setAi(p.key)}
                      className={`flex-1 px-2 py-1.5 rounded border text-[10px] font-mono transition ${
                        ai === p.key ? "text-white" : "text-gray-500 border-white/[0.06]"
                      }`}
                      style={ai === p.key ? { borderColor: `${p.color}55`, background: `${p.color}15`, color: p.color } : {}}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* 回答粘贴 */}
            <div>
              <div className="text-[8px] font-mono text-gray-600 mb-1">③ 粘贴 AI 回答（系统自动解析：提及/首条/前3/竞品/情感）</div>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={3}
                placeholder="把豆包/文心/通义/DeepSeek 对该问题的回答原文粘贴到这里..."
                className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40 resize-y font-mono"
              />
            </div>
            {/* 实时解析预览 */}
            {answer.trim() && (
              <div className="rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 flex items-center gap-1.5 flex-wrap">
                <span className="text-[8px] font-mono text-gray-600">实时解析：</span>
                {(() => {
                  const parsed = parseAnswer(answer, brandWords, competitors);
                  return (
                    <>
                      {renderBadge(parsed.mentioned, parsed.mentioned ? "已提及品牌" : "未提及品牌")}
                      {renderBadge(parsed.firstPosition, parsed.firstPosition ? "首条占位" : "未占首位", "#10B981", "#EF4444")}
                      {renderBadge(parsed.top3, parsed.top3 ? "前3推荐" : "未进前3", "#F59E0B", "#EF4444")}
                      <span
                        className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono"
                        style={{ background: `${sentimentMeta[judgeSentiment(answer)].color}15`, color: sentimentMeta[judgeSentiment(answer)].color }}
                      >
                        情感：{sentimentMeta[judgeSentiment(answer)].label}
                      </span>
                      {parsed.competitors.length > 0 && (
                        <span className="text-[8px] font-mono text-orange-300">
                          竞品出现：{parsed.competitors.join("、")}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <button
              onClick={handleSubmitQuery}
              disabled={parsing || !answer.trim()}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-cyan-600/80 hover:bg-cyan-600 text-[10px] font-mono text-white transition disabled:opacity-40"
            >
              {parsing ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
              {parsing ? "正在解析..." : "提交查询记录"}
            </button>
          </div>

          {/* 记录列表 */}
          {state.records.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/[0.08] p-6 text-center">
              <Eye size={16} className="text-gray-600 mx-auto mb-2" />
              <p className="text-[11px] text-gray-600">暂无查询记录。完成第一次主动查询后，这里将展示品牌在 AI 回答中的占位情况。</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {recentRecords.map((r) => {
                const p = AI_PLATFORMS.find((x) => x.key === r.ai)!;
                return (
                  <div key={r.id} className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-2.5">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${p.color}15`, color: p.color }}>
                          {p.name}
                        </span>
                        <span className="text-[10px] text-gray-300 truncate">{r.question}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {renderBadge(r.mentioned, "提及")}
                        {renderBadge(r.firstPosition, "首条", "#10B981", "#EF4444")}
                        {renderBadge(r.top3, "前3", "#F59E0B", "#EF4444")}
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ background: `${sentimentMeta[r.sentiment].color}15`, color: sentimentMeta[r.sentiment].color }}>
                          {sentimentMeta[r.sentiment].label}
                        </span>
                        <button onClick={() => handleDeleteRecord(r.id)} className="text-gray-600 hover:text-red-400">
                          <Trash2 size={9} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[9px] font-mono text-gray-600 leading-relaxed line-clamp-2 mb-1">{r.answer}</p>
                    {r.sources && r.sources.length > 0 && (
                      <p className="text-[8px] font-mono text-cyan-400/70 mb-1">🔗 信源：{r.sources.join("、")}</p>
                    )}
                    {r.ownArticles && r.ownArticles.length > 0 && (
                      <p className="text-[8px] font-mono text-emerald-400/80 mb-1">📄 官网文章被引：{r.ownArticles.join("、")}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] font-mono text-gray-600">
                        {new Date(r.checkedAt).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {r.competitors.length > 0 && (
                        <span className="text-[8px] font-mono text-orange-300">竞品出现：{r.competitors.join("、")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ Tab: 趋势分析 ============ */}
      {activeTab === "trend" && (
        <div className="space-y-3">
          {/* P0-8 按日期趋势（窗口可切换 14天/4周/8周/12周，数据来自服务端自动轮询每日落库） */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
                <TrendingUp size={10} className="text-blue-400" />
                提及率 / 首条占位率按日期趋势（{trendWindow === 14 ? "最近 14 天" : `${trendWindow / 7} 周`}）
              </div>
              <div className="flex items-center gap-0.5">
                {[14, 28, 56, 84].map((w) => (
                  <button
                    key={w}
                    onClick={() => setTrendWindow(w)}
                    className={`text-[8px] font-mono px-1.5 py-0.5 rounded transition-colors ${trendWindow === w ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "bg-white/[0.03] text-gray-500 border border-white/[0.06] hover:text-gray-300"}`}
                  >
                    {w === 14 ? "14天" : `${w / 7}周`}
                  </button>
                ))}
              </div>
            </div>
            {trendData.length === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">暂无数据，先去新增查询记录或跑一次自动轮询</p>
            ) : (
              <div className="flex items-end gap-1 h-[120px] border-b border-white/[0.06] pb-1">
                {trendData.map((d) => {
                  const maxH = 100;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${d.date}：提及 ${d.mentionRate}% / 首条 ${d.firstRate}%（${d.total} 次）`}>
                      <div className="w-full rounded-t bg-blue-500/70" style={{ height: `${Math.max(2, (d.mentionRate / 100) * maxH)}%` }} />
                      <div className="w-full rounded-t bg-emerald-500/70 -mt-1" style={{ height: `${Math.max(2, (d.firstRate / 100) * maxH)}%` }} />
                      <span className="text-[7px] font-mono text-gray-600 mt-0.5">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-3 mt-2 text-[9px] font-mono">
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-blue-500/70" />提及率</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-emerald-500/70" />首条占位率</span>
            </div>
          </div>

          {/* 平台维度 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <BarChart3 size={10} className="text-emerald-400" />
              各模型平台可见性对比（按平台分组的提及/首条/前3）
            </div>
            <div className="space-y-2">
              {platformStats.map((p) => (
                <div key={p.key} className="rounded border border-white/[0.04] bg-white/[0.01] p-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-mono" style={{ color: p.color }}>{p.name}</span>
                    <span className="text-[8px] font-mono text-gray-600">{p.stat.total} 次查询</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[
                      { label: "提及率", value: p.stat.total > 0 ? Math.round(p.stat.mentioned / p.stat.total * 100) : 0, color: "#3B82F6" },
                      { label: "首条占位", value: p.stat.total > 0 ? Math.round(p.stat.first / p.stat.total * 100) : 0, color: "#10B981" },
                      { label: "前3推荐", value: p.stat.total > 0 ? Math.round(p.stat.top3 / p.stat.total * 100) : 0, color: "#F59E0B" },
                    ].map((m, i) => (
                      <div key={i}>
                        <div className="text-[8px] font-mono text-gray-600 mb-0.5">{m.label}</div>
                        <div className="h-1.5 rounded bg-white/[0.04] overflow-hidden mb-1">
                          <div className="h-full rounded" style={{ width: `${m.value}%`, background: m.color }} />
                        </div>
                        <div className="text-[10px] font-mono font-bold" style={{ color: m.color }}>{m.value}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 最近记录时间线 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <TrendingUp size={10} className="text-blue-400" />
              最近查询时间线（最新 10 次）
            </div>
            {recentRecords.length === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">暂无数据，先去新增查询记录</p>
            ) : (
              <div className="space-y-1">
                {recentRecords.map((r, i) => {
                  const p = AI_PLATFORMS.find((x) => x.key === r.ai)!;
                  return (
                    <div key={r.id} className="flex items-center gap-2">
                      <span className="text-[8px] font-mono text-gray-700 w-8 flex-shrink-0">
                        {new Date(r.checkedAt).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit" })}
                      </span>
                      <span className="text-[8px] font-mono w-12 flex-shrink-0" style={{ color: p.color }}>{p.name}</span>
                      <div className="flex items-center gap-1 flex-1">
                        <div className="h-1.5 rounded bg-white/[0.04] overflow-hidden flex-1 max-w-[140px]">
                          <div className="h-full rounded bg-blue-500" style={{ width: `${r.mentioned ? 100 : 20}%`, opacity: r.mentioned ? 1 : 0.25 }} />
                        </div>
                        <span className="text-[8px] font-mono text-gray-500">{r.mentioned ? "提及" : "未提及"}</span>
                        {r.firstPosition && <span className="text-[8px] font-mono text-emerald-400">·首条</span>}
                        {r.top3 && <span className="text-[8px] font-mono text-amber-400">·前3</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* P0-8 信源质量评分：AI 采信信源的权威度加权 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Sparkles size={10} className="text-purple-400" />
              信源质量评分（AI 采信证据的权威度加权）
            </div>
            {!serverSourceQuality || serverSourceQuality.total === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">
                暂无信源质量数据。跑一次自动轮询后，按信源权威度（官网/公众号/百科/知乎 1.0 → 专业社区 0.7 → 社媒 0.4）自动评分。
              </p>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="relative w-16 h-16 flex-shrink-0">
                    <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3.5" />
                      <circle
                        cx="18" cy="18" r="15.5" fill="none"
                        stroke={serverSourceQuality.score >= 75 ? "#10B981" : serverSourceQuality.score >= 50 ? "#22D3EE" : serverSourceQuality.score >= 30 ? "#F59E0B" : "#EF4444"}
                        strokeWidth="3.5" strokeLinecap="round"
                        strokeDasharray={`${(serverSourceQuality.score / 100) * 97.4} 97.4`}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[13px] font-mono font-bold text-white">
                      {serverSourceQuality.score}
                    </span>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-gray-300">质量等级</span>
                      <span className="text-[10px] font-mono font-bold" style={{ color: serverSourceQuality.score >= 75 ? "#10B981" : serverSourceQuality.score >= 50 ? "#22D3EE" : serverSourceQuality.score >= 30 ? "#F59E0B" : "#EF4444" }}>
                        {serverSourceQuality.tier}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-mono text-gray-500">高质量覆盖（A+B）</span>
                      <span className="text-[10px] font-mono text-emerald-400">{serverSourceQuality.coverage}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-mono text-gray-500">去重信源数</span>
                      <span className="text-[10px] font-mono text-gray-300">{serverSourceQuality.total}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {(["A", "B", "C", "U"] as const).map((t) => {
                        const meta = { A: ["A·权威", "#10B981"], B: ["B·专业", "#22D3EE"], C: ["C·社媒", "#F59E0B"], U: ["其他", "#6B7280"] }[t];
                        return (
                          <span key={t} className="text-[7px] font-mono px-1 py-0.5 rounded" style={{ background: `${meta[1]}18`, color: meta[1] }}>
                            {meta[0]} {serverSourceQuality.tiers[t] || 0}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {serverSourceQuality.top.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {serverSourceQuality.top.map((s) => (
                      <span key={s.name} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                        {s.name} · {s.weight.toFixed(1)}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            <p className="text-[8px] font-mono text-gray-700 mt-2 leading-relaxed">
              💡 用法：评分越高 = AI 越倾向引用你的高权威信源（官网/公众号/百科/知乎）。若评分长期偏低，说明品牌内容还没进到权威阵地——
              优先把产品页/白皮书/百科词条结构化发布，再回看本评分变化。
            </p>
          </div>

          {/* P0-9 信源溯源：AI 回答引用的信源分布 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Link2 size={10} className="text-cyan-400" />
              信源溯源 Top（AI 回答中引用的 URL 域名 / 网站名）
            </div>
            {sourceStats.length === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">
                暂无信源记录。当 AI 回答中出现 https:// 链接或知乎/公众号/百度百科等信源时自动统计。
              </p>
            ) : (
              <div className="space-y-1.5">
                {sourceStats.map(([name, count], i) => {
                  const maxC = sourceStats[0][1];
                  return (
                    <div key={name} className="flex items-center gap-2">
                      <span className="text-[8px] font-mono text-gray-700 w-5 text-right">{i + 1}</span>
                      <span className="text-[10px] font-mono text-cyan-300/90 w-24 flex-shrink-0 truncate" title={name}>{name}</span>
                      <div className="flex-1 h-1.5 rounded bg-white/[0.04] overflow-hidden">
                        <div className="h-full rounded bg-cyan-500/60" style={{ width: `${Math.max(4, (count / maxC) * 100)}%` }} />
                      </div>
                      <span className="text-[9px] font-mono text-gray-500 w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[8px] font-mono text-gray-700 mt-2 leading-relaxed">
              💡 用法：信源即"AI 采信的证据"。若你的品牌信源（官网/公众号/知乎等）从未出现，说明内容尚未被模型索引——
              优先把结构化内容发布到这些高频信源阵地，再回看本面板变化。
            </p>
          </div>

          {/* P0-10 官网文章被引跟踪：官网全部文章何时被 AI 引用（服务端轮询自动检测） */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
                <FileText size={10} className="text-emerald-400" />
                官网文章被引跟踪（AI 是否引用官网具体文章）
              </div>
              {serverOwnArticleStats.length > 0 && (
                <span className="text-[9px] font-mono text-emerald-300">
                  被引 {serverOwnArticleStats.filter((s) => s.count > 0).length}/{serverOwnArticleStats.length} 篇
                </span>
              )}
            </div>
            {serverOwnArticleStats.length === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">
                暂无官网文章被引记录。官网全部文章已纳入监测清单，每日自动轮询检测 AI 回答中是否出现官网文章 URL/标题。
              </p>
            ) : (
              <div className="space-y-1">
                {serverOwnArticleStats.map((a) => {
                  const cited = a.count > 0;
                  return (
                    <div key={a.title} className={`flex items-center gap-2 rounded px-2 py-1 ${cited ? "bg-emerald-500/[0.06] border border-emerald-500/20" : "bg-white/[0.02] border border-white/[0.04]"}`}>
                      <span className={`text-[8px] font-mono w-3 flex-shrink-0 ${cited ? "text-emerald-400" : "text-gray-700"}`}>{cited ? "●" : "○"}</span>
                      <span className={`text-[9px] font-mono flex-1 truncate ${cited ? "text-emerald-300" : "text-gray-500"}`} title={a.title}>{a.title}</span>
                      <span className={`text-[9px] font-mono w-8 text-right flex-shrink-0 ${cited ? "text-emerald-400" : "text-gray-700"}`}>{a.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[8px] font-mono text-gray-700 mt-2 leading-relaxed">
              💡 用法：官网文章是提升 AI 找到我们的根源。被引计数 &gt; 0 = 该文章被 AI 在回答中引用过（按 URL/文件名/标题命中）。
              未被引用的文章说明尚未被模型索引，优先补充内链与同口径外部分发，再回看本面板。
            </p>
          </div>

          {/* P0-9 信源铺设指引：依据「AI 实际引用的信源」反推内容还缺哪些阵地（真实数据驱动） */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Target size={10} className="text-blue-400" />
              信源铺设指引（下一步去哪里铺内容）
            </div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[9px] font-mono text-emerald-300">已覆盖 {sourceCoverage.covered.length}</span>
              <span className="text-[9px] font-mono text-gray-600">|</span>
              <span className="text-[9px] font-mono text-amber-300">待铺设 {sourceCoverage.gaps.length}</span>
              <span className="text-[9px] font-mono text-gray-600">|</span>
              <span className="text-[9px] font-mono text-gray-500">基于 {sourceStats.length} 个被 AI 实际引用的信源</span>
            </div>
            <div className="space-y-1 mb-2">
              {SOURCE_CHANNELS.map((ch) => {
                const isGap = sourceCoverage.gaps.some((g) => g.label === ch.label);
                const tierColor = ch.tier === "A" ? "#10B981" : ch.tier === "B" ? "#22D3EE" : "#F59E0B";
                return (
                  <div key={ch.label} className={`flex items-center gap-2 rounded px-2 py-1 ${isGap ? "bg-amber-500/[0.06] border border-amber-500/15" : "bg-emerald-500/[0.04] border border-emerald-500/15"}`}>
                    <span className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0" style={{ background: `${tierColor}18`, color: tierColor }}>
                      {ch.tier}级
                    </span>
                    <span className={`text-[10px] truncate flex-1 ${isGap ? "text-amber-200/90" : "text-gray-400"}`}>{ch.label}</span>
                    <span className="text-[8px] font-mono text-gray-500 truncate hidden sm:inline">{ch.action}</span>
                    <span className={`text-[8px] font-mono flex-shrink-0 ${isGap ? "text-amber-400" : "text-emerald-400"}`}>
                      {isGap ? "待铺设" : "已进 AI 采信"}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="rounded bg-white/[0.02] border border-white/[0.05] p-2">
              <div className="text-[9px] font-mono text-gray-400 mb-1">行动建议（按优先级）</div>
              {sourceCoverage.gaps.length === 0 ? (
                <p className="text-[9px] font-mono text-emerald-400">全部阵地已有信源被 AI 引用，下一步：提升 A 级信源引用占比，回看上方质量评分。</p>
              ) : (
                <ol className="text-[9px] font-mono text-gray-500 space-y-0.5">
                  {sourceCoverage.gaps.slice(0, 5).map((g, i) => (
                    <li key={g.label}>{i + 1}. <span className="text-gray-300">{g.label}</span> —— {g.action}</li>
                  ))}
                </ol>
              )}
            </div>
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent("xeaos:goto-tab", { detail: "source" })); } catch { /* ignore */ } }}
              className="mt-2 flex items-center gap-1 px-2.5 py-1.5 rounded text-[9px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 transition"
            >
              <Zap size={9} /> 去「信源铺设」登记发布计划
            </button>
          </div>
        </div>
      )}

      {/* ============ Tab: 竞品对比 ============ */}
      {activeTab === "compete" && (
        <div className="space-y-3">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Swords size={10} className="text-orange-400" />
              AI 回答中竞品出现统计（品牌 vs 竞品的 AI 认知对比）
            </div>
            {competitorStats.length === 0 ? (
              <p className="text-[9px] font-mono text-gray-600 text-center py-3">
                暂无竞品出现记录。当 AI 回答中出现竞品词时会自动统计，可在「问题库/设置」中配置竞品词。
              </p>
            ) : (
              <div className="space-y-2">
                {competitorStats.map((c) => (
                  <div key={c.name} className="rounded border border-white/[0.04] bg-white/[0.01] p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-orange-300">{c.name}</span>
                      <span className="text-[9px] font-mono text-gray-500">{c.count} 次 / {c.rate}%</span>
                    </div>
                    <div className="h-1.5 rounded bg-white/[0.04] overflow-hidden">
                      <div className="h-full rounded bg-orange-500/70" style={{ width: `${Math.min(100, c.rate * 2)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* 品牌自身占位对比 */}
            <div className="mt-3 pt-3 border-t border-white/[0.05]">
              <div className="text-[9px] font-mono text-gray-500 mb-2">品牌自身（{metrics.total} 次查询）</div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { label: "提及率", value: metrics.mentionRate, color: "#3B82F6" },
                  { label: "首条占位", value: metrics.firstRate, color: "#10B981" },
                  { label: "前3推荐", value: metrics.top3Rate, color: "#F59E0B" },
                  { label: "心智占有", value: metrics.mindShare, color: "#A855F7" },
                ].map((m, i) => (
                  <div key={i} className="rounded border border-white/[0.04] p-1.5">
                    <div className="text-[8px] font-mono text-gray-600">{m.label}</div>
                    <div className="text-[13px] font-bold font-mono" style={{ color: m.color }}>{m.value}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ Tab: 策略建议 ============ */}
      {activeTab === "advice" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Lightbulb size={10} className="text-purple-400" />
              根据可见性数据 → 反推内容策略（GEO 完整链路闭环）
            </div>
            {advice.map((a, i) => {
              const color = a.level === "good" ? "#10B981" : a.level === "warn" ? "#F59E0B" : "#EF4444";
              return (
                <div key={i} className="rounded border p-2.5 mb-2" style={{ borderColor: `${color}22`, background: `${color}06` }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                    <span className="text-[10px] font-medium" style={{ color }}>{a.title}</span>
                  </div>
                  <p className="text-[9px] font-mono text-gray-500 mb-1">{a.detail}</p>
                  <p className="text-[9px] font-mono" style={{ color }}>→ {a.action}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ Tab: 反哺闭环（0提及泛词 → 内容选题 + 信源铺设） ============ */}
      {activeTab === "feedback" && (
        <div className="space-y-3">
          {/* 说明 + 汇总 */}
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.03] p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <RefreshCw size={10} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-300">监测反哺闭环：0 提及泛词 → 内容选题 → 生成 → 信源铺设</span>
            </div>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed">
              每次「一键诊断」完成后，全平台都未提及品牌的问题会自动进入这里 = 内容缺口。为它生成一篇可被 AI 引用的事实型文章，
              发布到知乎/公众号/小红书等信源后打勾标记，再回去重新诊断，就能看到提及率上升 —— 这就是 GEO 转起来的核心循环。
            </p>
            <div className="mt-2 flex items-center gap-3 text-[9px] font-mono flex-wrap">
              <span className="text-emerald-400">缺口选题 {gapSummary.total}</span>
              <span className="text-blue-400">已生成内容 {gapSummary.hasContent}</span>
              <span className="text-amber-400">待铺设 {gapSummary.ready}</span>
              <span className="text-gray-500">已铺设信源 {gapSummary.published}</span>
            </div>
          </div>

          {/* 手动录入 */}
          <div className="flex items-center gap-2">
            <input
              value={gapCustom}
              onChange={(e) => setGapCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addGapManual(); }}
              placeholder="手动录入泛词选题，如：长沙企业AI获客哪家好"
              className="flex-1 px-2 py-1 text-[10px] font-mono bg-white/[0.03] border border-white/[0.08] rounded focus:outline-none focus:border-emerald-500/40 placeholder-gray-600 text-gray-300"
            />
            <button
              onClick={addGapManual}
              disabled={!gapCustom.trim()}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono text-white bg-emerald-600/70 hover:bg-emerald-600 border border-emerald-500/40 transition disabled:opacity-40"
            >
              <Plus size={10} />
              添加选题
            </button>
          </div>

          {/* 选题列表 */}
          {gapTopics.length === 0 ? (
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.01] p-4 text-center text-[9px] font-mono text-gray-600">
              暂无缺口选题 —— 跑一次「一键诊断」后，0 提及问题会自动出现在这里。
            </div>
          ) : (
            <div className="space-y-2">
              {gapTopics.map((g) => {
                const hasContent = g.content.length > 0;
                const generating = gapGenerating === g.id;
                return (
                  <div key={g.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-mono text-gray-200 break-all">{g.question}</span>
                          {g.platforms.length > 0 && (
                            <span className="text-[8px] font-mono text-red-400/80">0提及×{g.platforms.length}</span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[8px] font-mono text-gray-600 flex-wrap">
                          <span className={hasContent ? "text-emerald-400" : ""}>{hasContent ? "✓ 已生成内容" : "待生成内容"}</span>
                          <span>铺设：{g.publishedSources.length ? g.publishedSources.join(" / ") : "未铺设"}</span>
                          <span>录入 {g.createdAt.slice(0, 10)}</span>
                        </div>
                        {/* 信源铺设 */}
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                          <span className="text-[8px] font-mono text-gray-600">信源铺设：</span>
                          {GAP_SOURCES.map((src) => {
                            const on = g.publishedSources.includes(src);
                            return (
                              <button
                                key={src}
                                onClick={() => toggleGapSource(g.id, src)}
                                className={`px-1.5 py-0.5 rounded text-[8px] font-mono border transition-colors ${
                                  on ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-gray-500 hover:text-gray-300 border-white/[0.06]"
                                }`}
                                title={on ? `已铺设到${src}（点击取消）` : `标记已发布到${src}`}
                              >
                                {on ? "✓ " : ""}{src}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={() => generateGapContent(g)}
                          disabled={generating}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-white bg-cyan-600/70 hover:bg-cyan-600 border border-cyan-500/40 transition disabled:opacity-40"
                        >
                          {generating ? <Loader2 size={9} className="animate-spin" /> : <PenLine size={9} />}
                          {generating ? "生成中…" : hasContent ? "重新生成" : "生成内容"}
                        </button>
                        <button
                          onClick={() => deleteGap(g.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-red-400/80 hover:text-red-300 border border-red-500/20 hover:border-red-500/40 transition"
                        >
                          <Trash2 size={9} />
                          删除
                        </button>
                      </div>
                    </div>
                    {hasContent && (
                      <details className="mt-2">
                        <summary className="text-[8px] font-mono text-gray-600 cursor-pointer hover:text-gray-400">查看生成内容草稿（可复制去各平台发布）</summary>
                        <pre className="mt-1 text-[9px] text-gray-400 whitespace-pre-wrap break-words leading-relaxed max-h-[260px] overflow-y-auto rounded bg-white/[0.02] border border-white/[0.05] p-2">{g.content}</pre>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ Tab: 问题库/设置 ============ */}
      {activeTab === "settings" && (
        <div className="space-y-3">
          {/* ===== P0-7 服务端自动轮询 ===== */}
          <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.03] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-1 flex items-center gap-1.5">
              <Radar size={10} className="text-purple-300" />
              自动轮询（服务端定时跑 DeepSeek，每日自动建立可见性基线 + 周报）
            </div>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed mb-2">
              开启后服务器每小时检查，到间隔时长自动用 8 条标准清单跑豆包/文心/通义/DeepSeek，结果落 vault/XEAOS/AI可见性记录，每周一自动生成周报。
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={toggleAutoPoll}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white transition ${aivisEnabled ? "bg-emerald-600/80 hover:bg-emerald-600" : "bg-gray-600/60 hover:bg-gray-600"}`}
              >
                {aivisEnabled ? "✓ 已开启自动轮询" : "开启自动轮询"}
              </button>
              <button
                onClick={runPollNow}
                disabled={pollRunning}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-purple-500/30 hover:bg-purple-500/40 border border-purple-500/40 transition disabled:opacity-40"
              >
                {pollRunning ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                {pollRunning ? "轮询中…" : "立即跑一次"}
              </button>
              <span className="flex items-center gap-1 text-[9px] font-mono text-gray-500">
                间隔
                <select
                  value={aivisInterval}
                  onChange={(e) => changeInterval(Number(e.target.value))}
                  className="bg-white/[0.06] border border-white/10 rounded px-1 py-0.5 text-[9px] font-mono text-gray-300 focus:outline-none"
                >
                  {[6, 12, 24, 48, 72, 168].map((h) => (
                    <option key={h} value={h} className="bg-[#0B1220] text-gray-300">
                      {h >= 24 ? (h / 24 === 1 ? "24h(日)" : h / 24 === 7 ? "168h(周)" : `${h / 24}d`) : `${h}h`}
                    </option>
                  ))}
                </select>
              </span>
              {aivisLastRun && <span className="text-[9px] font-mono text-gray-600">上次：{aivisLastRun}</span>}
            </div>
            {aivisLatest && aivisLatest.date && (
              <div className="mt-2 flex items-center gap-3 text-[9px] font-mono">
                <span className="text-gray-500">最近一次（{aivisLatest.date}）：</span>
                <span className="text-blue-400">提及 {aivisLatest.mentionRate ?? 0}%</span>
                <span className="text-emerald-400">首条 {aivisLatest.firstRate ?? 0}%</span>
                <span className="text-amber-400">前3 {aivisLatest.top3Rate ?? 0}%</span>
                <span className="text-gray-600">（{aivisLatest.ok ?? 0}/{aivisLatest.total ?? 0}）</span>
              </div>
            )}
          </div>

          {/* ===== v5.2 审计补齐：GEO 基线测试（白皮书第十八章⑨"哪家靠谱"模板） ===== */}
          <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-1 flex items-center gap-1.5">
              <Target size={10} className="text-cyan-400" />
              GEO 基线测试（每周跑一轮，用真实地域/行业问题测品牌在 AI 中的占位）
            </div>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed mb-2">
              模板：｛城市｝｛行业｝哪家靠谱 / ｛城市｝做｛行业｝哪家好。自动读取「信源入库」的地域 GEO 与业务关键词生成，
              建议每周固定时间批量问豆包/文心/通义/DeepSeek 并粘贴回答，形成可对比的基线序列。
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={generateBaselineQuestions}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-cyan-600/80 hover:bg-cyan-600 text-[10px] font-mono text-white transition"
              >
                <Sparkles size={10} /> 生成基线问题（地域 × 行业）
              </button>
              <span className="text-[9px] font-mono text-gray-600">
                已生成 {baselineCount} 条（自动加入下方问题库）
              </span>
            </div>
          </div>

          {/* ===== P0-6 GEO 可见性标准提问清单 ===== */}
          <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-1 flex items-center gap-1.5">
              <Radar size={10} className="text-cyan-400" />
              GEO 可见性标准清单（8 条固定问题，一键加载到问题库 / 一键跑全套）
            </div>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed mb-2">
              覆盖"GEO 是什么 / 服务商 / GEO+DSO 平台 / 小舍AI / XEAOS / 企业 AI 系统 / 酒店 AI"等高频提问。
              加载后可在上方「一键诊断」里点「一键跑全套」对勾选平台批量跑，建立可见性基线。
            </p>
            <button
              onClick={loadPresetQuestions}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-cyan-600/80 hover:bg-cyan-600 text-[10px] font-mono text-white transition"
            >
              <Plus size={10} /> 加载标准清单到问题库
            </button>
          </div>

          {/* 问题库 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Settings2 size={10} className="text-gray-500" />
              关键问题库（定期拿去问各家大模型的问题清单）
            </div>
            <div className="flex gap-1.5 mb-2">
              <input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddQuestion()}
                placeholder="新增关键问题，如：XEAOS 和同类工具比有什么优势？"
                className="flex-1 px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-cyan-500/40"
              />
              <button
                onClick={handleAddQuestion}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-cyan-600/70 hover:bg-cyan-600 text-[10px] font-mono text-white transition"
              >
                <Plus size={10} /> 添加
              </button>
            </div>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {state.questions.length === 0 ? (
                <p className="text-[9px] font-mono text-gray-600 text-center py-2">暂无问题，已自动从品牌词生成的问题会在此显示</p>
              ) : (
                state.questions.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-white/[0.02]">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {q.auto ? (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-purple-500/10 text-purple-300 flex-shrink-0">自动</span>
                      ) : (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-300 flex-shrink-0">手动</span>
                      )}
                      <span className="text-[10px] text-gray-300 truncate">{q.question}</span>
                    </div>
                    {!q.auto && (
                      <button onClick={() => handleDeleteQuestion(q.id)} className="text-gray-600 hover:text-red-400 flex-shrink-0">
                        <Trash2 size={9} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 竞品配置 */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-400 mb-2 flex items-center gap-1.5">
              <Swords size={10} className="text-orange-400" />
              竞品词配置（AI 回答中出现这些词时自动统计）
            </div>
            <div className="flex flex-wrap gap-1.5">
              {competitors.map((c, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono bg-orange-500/10 text-orange-300 border border-orange-500/20">
                  {c}
                  <button onClick={() => setCompetitors((prev) => prev.filter((_, j) => j !== i))} className="text-orange-400/60 hover:text-red-400">
                    <XCircle size={8} />
                  </button>
                </span>
              ))}
              <input
                placeholder="+ 添加竞品词"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = (e.target as HTMLInputElement).value.trim();
                    if (v && !competitors.includes(v)) setCompetitors((prev) => [...prev, v]);
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
                className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[9px] text-gray-300 outline-none focus:border-orange-500/40 w-28"
              />
            </div>
          </div>

          {/* 品牌词提示 */}
          <div className="rounded-lg border border-dashed border-white/[0.08] p-3 flex items-start gap-2">
            <AlertCircle size={11} className="text-cyan-400 mt-0.5 flex-shrink-0" />
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed">
              品牌识别词（{brandWords.join("、")}）自动同步自「信源入库」的品牌词监控池（xeaos_geo_input）。
              如需调整，请到 GEO 输入层的品牌词监控池维护。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
